import { CalendarDays, Flame } from 'lucide-react';
import { useMemo, useState, type CSSProperties } from 'react';
import type { Habit } from '../../shared/api';
import type { DayKey } from '../../shared/days';
import { scheduleLabel, streaks, weekCount } from '../../shared/schedule';
import { streakText } from '../lib/format';
import { tap } from '../lib/haptics';
import { paths } from '../lib/router';
import { EmojiTile, HabitGlyph } from './EmojiTile';
import { Link } from './Link';

export interface HabitRowProps {
  habit: Habit;
  days: ReadonlySet<DayKey>;
  day: DayKey;
  today: DayKey;
  onToggle: (count: number) => void;
  count?: number;
  /** Not planned for the day: shown quieter. */
  muted?: boolean;
  index?: number;
}


/** Up to this many completions per day the symbols are shown large, above in a compact size. */
const MINI_LARGE_MAX = 6;
export function HabitRow({ habit, days, day, today, onToggle, count = Number(days.has(day)), muted, index = 0 }: HabitRowProps) {
  const target = habit.schedule.kind === 'daily' ? habit.schedule.times ?? 1 : 1;
  const done = count >= target;
  const streak = useMemo(() => streaks(habit, days, today), [habit, days, today]);
  const [pop, setPop] = useState(false);
  const weekly = habit.schedule.kind === 'weekly' ? { count: weekCount(days, day), target: habit.schedule.times } : null;

  const toggle = () => {
    if (!done) {
      setPop(true);
      tap(10);
    } else {
      tap(4);
    }
    onToggle(done ? 0 : Math.min(target, count + 1));
  };

  return (
    <li
      className={`habit glass hc-${habit.color}${done ? ' habit--done' : ''}${muted ? ' habit--muted' : ''}`}
      style={{ '--i': index } as CSSProperties}
    >
      <button type="button" className="habit__toggle" aria-pressed={done} aria-label={`${habit.name}: ${Math.min(count, target)} von ${target} heute erledigt`} onClick={toggle}>
        <EmojiTile emoji={habit.emoji} sticker={habit.sticker} color={habit.color} />
        <span className="habit__body">
          <span className="habit__name">{habit.name}</span>
          <span className="habit__meta">
            {streak.current > 0 && (
              <span className="habit__streak" title="Aktuelle Serie">
                <Flame size={14} aria-hidden="true" />
                {streakText(streak.current, streak.unit)}
              </span>
            )}
            {weekly ? (
              <span className="habit__weekly" title="Diese Woche">
                <span className="dots" aria-hidden="true">
                  {Array.from({ length: weekly.target }, (_, i) => (
                    <span key={i} className={`dots__dot${i < weekly.count ? ' dots__dot--on' : ''}`} />
                  ))}
                </span>
                <span aria-hidden="true">
                  {Math.min(weekly.count, weekly.target)}/{weekly.target} Woche
                </span>
                <span className="sr-only">
                  {Math.min(weekly.count, weekly.target)} von {weekly.target} diese Woche
                </span>
              </span>
            ) : (
              <span className="habit__weekly">
                <span>{scheduleLabel(habit.schedule)}</span>
                {target > 1 && (
                  <span className={`mini-emojis${target > MINI_LARGE_MAX ? ' mini-emojis--many' : ''}`} aria-hidden="true">
                    {Array.from({ length: target }, (_, i) => (
                      <HabitGlyph
                        key={i}
                        emoji={habit.emoji}
                        sticker={habit.sticker}
                        className={`mini-emojis__item${i < count ? ' mini-emojis__item--on' : ''}`}
                      />
                    ))}
                  </span>
                )}
              </span>
            )}
          </span>
        </span>
        <span className={`check${pop ? ' check--pop' : ''}`} onAnimationEnd={() => setPop(false)} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <path d="M5.5 12.5l4.2 4.2 8.8-9.4" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      <Link to={paths.habit(habit.id)} className="habit__open" aria-label={`Kalender: ${habit.name}`} title="Kalender">
        <CalendarDays size={20} />
      </Link>
    </li>
  );
}
